import React, { useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import useUiuxStore from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { ShowSuccessFetch, showSuccess, showWarning } from 'src/utils/toast/toastUtil';
import { getHRM3050_R01List } from '../HRM3050_R01/Api_HRM3050_R01';
import { getHRM2130_R01List } from '../HRM2130_R01/Api_HRM2130_R01';
import {
    downloadExcel_HRM2010_R01,
    getHRM2010_R01DetailList,
    getHRM2010_R01List,
    patchHRM2010_R01,
    postHRM2010_R01Bulk,
} from './Api_HRM2010_R01';
import {
    defaultHRM2010_R01DetailDto,
    defaultHRM2010_R01Dto,
    HRM2010_R01DetailDto,
    HRM2010_R01HeaderDto,
} from './Dto_HRM2010_R01';
import { useHRM2010_R01CellChange } from './hooks/HRM2010_R01CellChange';
import { HRM2010_R01Filter, useHRM2010_R01Store } from './Store_HRM2010_R01';
import { isHRM2010_R01UnsavedRow, validateHRM2010_R01SaveData } from './Util_HRM2010_R01';

/** 조회·편집 그리드 참조와 하단 메시지 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM2010_R01HeaderDto> | null>;
    detailGridRef: React.RefObject<AgGridReact<HRM2010_R01DetailDto> | null>;
    setMessage: (value: string) => void;
}

/** 검색 조건의 빈 값은 서버에서 전체 조건으로 처리한다. */
const toParams = (filter: HRM2010_R01Filter): Partial<HRM2010_R01Filter> => ({
    workDt: filter.workDt,
    compCd: filter.compCd || undefined,
    orgCd: filter.orgCd || undefined,
    attendCd: filter.attendCd || undefined,
    outYn: filter.outYn || undefined,
    shiftCd: filter.shiftCd || undefined,
});

/** 일일근태 조회·저장·외출 기록·일괄 등록 동작을 연결한다. */
export const HRM2010_R01Content = ({ gridRef, detailGridRef, setMessage }: Props) => {
    /** 첫 조회 성공 여부를 저장해 조회 전 일괄·Excel 실행을 막는다. */
    const hasFetched = useRef(false);
    const { setConfirmModalProps, setConfirmModalVisible } = useUiuxStore();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const { calculateHRM2010_R01Row } = useHRM2010_R01CellChange();
    useFetchActionbarAuth('HRM2010_R01');
    /** 공통 확인창을 닫고 속성을 비운다. */
    const closeConfirmModal = useCallback(() => {
        setConfirmModalVisible(false);
        setConfirmModalProps(undefined);
    }, [setConfirmModalProps, setConfirmModalVisible]);
    /** 화면 전용 근무조와 근태코드를 조회한다. */
    const loadMasters = useCallback(async () => {
        const [shifts, codes] = await Promise.all([
            getHRM3050_R01List('search', {}),
            getHRM2130_R01List('search', {}),
        ]);
        const state = useHRM2010_R01Store.getState();
        if (shifts.data.successFlag) {
            state.setWorkShiftList(shifts.data.data.filter(row => row.useYn === 'Y'));
        }
        if (codes.data.successFlag) {
            state.setAttendCodeList(codes.data.data.filter(row => row.useYn === 'Y'));
        }
    }, []);

    useEffect(() => {
        void loadMasters();
    }, [loadMasters]);

    /** 선택한 사원의 저장된 외출 기록을 조회한다. */
    const loadDetails = useCallback(async (row?: HRM2010_R01HeaderDto) => {
        const state = useHRM2010_R01Store.getState();
        if (!row?.empNo) {
            state.setHRM2010_R01DetailList([]);
            state.setOriginalHRM2010_R01DetailList([]);
            return;
        }
        const response = await getHRM2010_R01DetailList(row.empNo, row.workDt);
        if (!response.data.successFlag) return;
        state.setHRM2010_R01DetailList(response.data.data);
        state.setOriginalHRM2010_R01DetailList(response.data.data);
    }, []);
    /** 위·아래 그리드의 변경 여부를 판단한다. */
    const hasChanges = useCallback((): boolean => {
        const state = useHRM2010_R01Store.getState();
        return state.HRM2010_R01List.some(row => row.isModified)
            || state.deletedHRM2010_R01List.length > 0
            || state.HRM2010_R01DetailList.some(row => row.inserted || row.isModified)
            || state.deletedHRM2010_R01DetailList.length > 0;
    }, []);
    /** 아래 외출 기록만 변경됐는지 판단한다. */
    const hasDetailChanges = useCallback((): boolean => {
        const state = useHRM2010_R01Store.getState();
        return state.HRM2010_R01DetailList.some(row => row.inserted || row.isModified)
            || state.deletedHRM2010_R01DetailList.length > 0;
    }, []);
    /** 저장하지 않은 변경을 버릴지 공통 확인창에서 묻는다. */
    const confirmDiscard = useCallback((onConfirm: () => void, detailOnly = false) => {
        const hasUnsavedChanges = detailOnly ? hasDetailChanges() : hasChanges();
        if (!hasUnsavedChanges) {
            onConfirm();
            return;
        }

        setConfirmModalProps({
            title: '변경 내용 확인',
            message: '저장하지 않은 변경 내용이 있습니다.\n변경 내용을 버리고 진행하시겠습니까?',
            confirmText: '확인',
            cancelText: '취소',
            onConfirm: () => {
                closeConfirmModal();
                onConfirm();
            },
            onCancel: closeConfirmModal,
        });
        setConfirmModalVisible(true);
    }, [hasChanges, hasDetailChanges, closeConfirmModal, setConfirmModalProps, setConfirmModalVisible]);
    /** 검색 조건으로 목록을 조회하고 직전 선택 사원 또는 첫 행을 선택한다. */
    const executeDataFetch = useCallback(async (option: 'search' | 'refetch', empNo?: string, quiet = false) => {
        setLoadingSpinner(true);
        try {
            const state = useHRM2010_R01Store.getState();
            // 삼항 연산자: 재조회(refetch)는 마지막 조회 조건을, 일반 조회(search)는 현재 화면 조건을 쓴다.
            // 저장·일괄 정상출근은 마지막 조회 조건의 근무일자로 실행되므로, 조회 후 검색 조건만 바꾼 상태에서도
            // 방금 처리한 날짜의 목록을 다시 보여 주기 위해서이다.
            const filter = option === 'refetch' ? state.HRM2010_R01PastFilter : state.HRM2010_R01Filter;
            const response = await getHRM2010_R01List(option, toParams(filter));
            if (!response.data.successFlag) return;
            hasFetched.current = true;
            const previousEmpNo = empNo || state.selectedHRM2010_R01?.empNo;
            if (option === 'search') state.setScrollTop(null);
            state.setHRM2010_R01List(response.data.data);
            state.setOriginalHRM2010_R01List(response.data.data);
            const current = useHRM2010_R01Store.getState();
            const selected = current.HRM2010_R01List.find(row => row.empNo === previousEmpNo)
                || current.HRM2010_R01List[0];
            current.setSelectedHRM2010_R01(selected);
            current.setHRM2010_R01PastFilter(filter);
            await loadDetails(selected);
            if (quiet) return;
            if (!response.data.data.length) {
                showWarning('데이터가 없습니다.');
                return;
            }
            ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${response.data.data.length}건)`);
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, loadDetails, setMessage]);
    /** 조회 전에 저장하지 않은 변경을 확인한다. */
    const fetchData = useCallback(() => confirmDiscard(() => {
        void executeDataFetch('search');
    }), [confirmDiscard, executeDataFetch]);
    /** 다른 사원을 선택하면 외출 변경을 되돌리고 선택 사원의 기록을 조회한다. */
    const handleHeaderRowClick = useCallback((row: HRM2010_R01HeaderDto) => {
        const state = useHRM2010_R01Store.getState();
        if (state.selectedHRM2010_R01?.valueIndex === row.valueIndex) return;
        confirmDiscard(() => {
            const current = useHRM2010_R01Store.getState();
            const previous = current.selectedHRM2010_R01;
            if (previous && hasDetailChanges()) {
                const original = current.originalHRM2010_R01DetailList;
                void calculateHRM2010_R01Row(previous, original);
            }
            current.setSelectedHRM2010_R01(row);
            current.setActiveGridType('header');
            void loadDetails(row);
        }, true);
    }, [confirmDiscard, hasDetailChanges, calculateHRM2010_R01Row, loadDetails]);
    /** 외출 기록 변경 후 위 행을 서버에서 다시 계산한다. */
    const recalculateSelected = useCallback(() => {
        const state = useHRM2010_R01Store.getState();
        if (!state.selectedHRM2010_R01) return;
        const activeEvents = state.HRM2010_R01DetailList.filter(row =>
            !state.deletedHRM2010_R01DetailList.includes(String(row.valueIndex)));
        void calculateHRM2010_R01Row(state.selectedHRM2010_R01, activeEvents);
    }, [calculateHRM2010_R01Row]);
    /** 선택한 사원에게 빈 시각의 외출·복귀 행을 추가한다. */
    const handleEventAddClick = useCallback((attendCd: string, afterValueIndex?: number) => {
        const state = useHRM2010_R01Store.getState();
        const selected = state.selectedHRM2010_R01;
        if (!selected) {
            showWarning('일일근태 행을 먼저 선택하세요.');
            return;
        }
        if (!selected.empNo) {
            showWarning('사원을 먼저 선택하세요.');
            return;
        }

        const code = state.attendCodeList.find(row => row.attendCd === attendCd);
        const nextEventSeq = Math.max(0, ...state.HRM2010_R01DetailList.map(row => row.eventSeq)) + 1;
        state.insertHRM2010_R01DetailList({
            ...defaultHRM2010_R01DetailDto,
            empNo: selected.empNo,
            workDt: selected.workDt,
            eventSeq: nextEventSeq,
            attendCd,
            attendNm: code?.attendNm || '',
            attendGroup: code?.attendGroup || '',
            workYn: code?.workYn || '',
            eventTime: '',
            inserted: true,
        }, afterValueIndex);
        state.setActiveGridType('detail');
        recalculateSelected();
        const nextIndex = afterValueIndex === undefined
            ? state.HRM2010_R01DetailList.length
            : state.HRM2010_R01DetailList.findIndex(row => row.valueIndex === afterValueIndex) + 1;
        setTimeout(() => detailGridRef.current?.api.startEditingCell({ rowIndex: nextIndex, colKey: 'eventTime' }), 0);
    }, [detailGridRef, recalculateSelected]);
    /** index 삽입은 아래 그리드의 직전 구분에 맞게 외출 또는 복귀를 만든다. */
    const handleDetailInsert = useCallback((event: React.MouseEvent, rowIndex?: number) => {
        event.stopPropagation();
        const state = useHRM2010_R01Store.getState();
        const previous = rowIndex === undefined
            ? state.HRM2010_R01DetailList.at(-1)
            : state.HRM2010_R01DetailList[rowIndex];
        handleEventAddClick(previous?.attendGroup === 'G5' ? 'O03' : 'O01', previous?.valueIndex);
    }, [handleEventAddClick]);
    /** 외출 신규 행은 제거하고 저장된 외출·일일근태 행은 삭제 표시하거나 복구한다. */
    const handleDeleteClick = useCallback(() => {
        const state = useHRM2010_R01Store.getState();
        if (state.activeGridType === 'detail') {
            const ids = state.selectedHRM2010_R01DetailList;
            const existing = ids.filter(id => state.HRM2010_R01DetailList.some(row =>
                String(row.valueIndex) === id && !row.inserted));
            const inserted = state.HRM2010_R01DetailList.filter(row =>
                row.inserted && ids.includes(String(row.valueIndex)));
            inserted.forEach(row => state.removeHRM2010_R01DetailList(row.valueIndex));

            const allMarked = existing.length > 0
                && existing.every(id => state.deletedHRM2010_R01DetailList.includes(id));
            state.setDeletedHRM2010_R01DetailList(allMarked
                ? state.deletedHRM2010_R01DetailList.filter(id => !existing.includes(id))
                : Array.from(new Set([...state.deletedHRM2010_R01DetailList, ...existing])));
            state.setSelectedHRM2010_R01DetailList([]);
            recalculateSelected();
            return;
        }
        const selected = state.selectedHRM2010_R01;
        if (!selected) return;
        // 미입력 행은 DB에 없으므로 삭제 대상이 아니다. 입력을 취소하려면 다시 조회한다.
        if (selected.isAttendMissing) {
            showWarning('저장되지 않은 미입력 행은 삭제할 수 없습니다.');
            return;
        }
        const id = String(selected.valueIndex);
        state.setDeletedHRM2010_R01List(state.deletedHRM2010_R01List.includes(id)
            ? state.deletedHRM2010_R01List.filter(value => value !== id)
            : [...state.deletedHRM2010_R01List, id]);
    }, [recalculateSelected]);
    /** 변경된 위 행과 선택 사원의 외출 전체 기록만 서버에 저장한다. */
    const handleSaveClick = useCallback(async () => {
        gridRef.current?.api.stopEditing();
        detailGridRef.current?.api.stopEditing();
        const state = useHRM2010_R01Store.getState();
        if (!hasChanges()) {
            showWarning('저장할 내용이 없습니다.');
            return;
        }

        const eventsChanged = hasDetailChanges();
        const activeEvents = state.HRM2010_R01DetailList.filter(row =>
            !state.deletedHRM2010_R01DetailList.includes(String(row.valueIndex)));
        // 편집하지 않은 미입력 행은 저장하지 않으므로 근태 필수 검사에서 뺀다.
        const error = validateHRM2010_R01SaveData(
            state.HRM2010_R01List.filter(row => !isHRM2010_R01UnsavedRow(row)),
            eventsChanged ? activeEvents : [],
            state.deletedHRM2010_R01List,
        );
        if (error) {
            showWarning(error);
            return;
        }

        // 편집한 미입력 행은 DB에 없으므로 저장 요청에서만 inserted를 붙여 서버의 신규 등록 경로로 보낸다.
        // 화면 목록에는 inserted를 두지 않아 공통 신규 행 스타일(녹색·I 표시)이 붙지 않게 한다.
        const changedDailyRows = state.HRM2010_R01List.filter(row =>
            !state.deletedHRM2010_R01List.includes(String(row.valueIndex))
            && row.isModified)
            .map(row => ({ ...row, inserted: !!row.isAttendMissing }));
        const deletedEmpNos = state.HRM2010_R01List.filter(row =>
            state.deletedHRM2010_R01List.includes(String(row.valueIndex)))
            .map(row => row.empNo);

        setLoadingSpinner(true);
        try {
            const response = await patchHRM2010_R01({
                ...defaultHRM2010_R01Dto,
                workDt: state.HRM2010_R01PastFilter.workDt,
                hrmAttendDailyDtoList: changedDailyRows,
                deletedEmpNoList: deletedEmpNos,
                eventEmpNo: eventsChanged ? state.selectedHRM2010_R01?.empNo || '' : '',
                hrmAttendEventDtoList: eventsChanged ? activeEvents : null,
            });
            if (!response.data.successFlag) return;
            showSuccess('일일근태 저장이 완료되었습니다.');
            await executeDataFetch('refetch', state.selectedHRM2010_R01?.empNo, true);
        } finally {
            setLoadingSpinner(false);
        }
    }, [gridRef, detailGridRef, hasChanges, hasDetailChanges, setLoadingSpinner, executeDataFetch]);

    /** 일괄 정상출근을 적용하고 생성 건수를 알린 뒤 목록을 다시 조회한다. */
    const applyBulk = useCallback(async (workDt: string, selectedEmpNo?: string) => {
        setLoadingSpinner(true);
        try {
            const response = await postHRM2010_R01Bulk({
                ...defaultHRM2010_R01Dto,
                workDt,
            });
            if (!response.data.successFlag) return;

            const { createdCount, skippedEmpNmList } = response.data.data;
            if (!createdCount) {
                showWarning('근태 미입력 재직자가 없습니다.');
                return;
            }

            const skipped = skippedEmpNmList.length
                ? ` 근무조를 정할 수 없어 제외: ${skippedEmpNmList.join(', ')}`
                : '';
            const message = `근태 미입력 재직자 ${createdCount}명에게 정상출근을 만들었습니다.${skipped}`;
            showSuccess(message);
            setMessage(message);
            await executeDataFetch('refetch', selectedEmpNo, true);
        } finally {
            setLoadingSpinner(false);
        }
    }, [setLoadingSpinner, setMessage, executeDataFetch]);

    /** 일괄 정상출근 확인 뒤 전체 재직자를 서버에서 생성한다. */
    const handleBulkClick = useCallback(() => {
        if (!hasFetched.current) {
            showWarning('먼저 조회하세요.');
            return;
        }

        confirmDiscard(() => {
            const current = useHRM2010_R01Store.getState();
            setConfirmModalProps({
                title: '일괄 정상출근',
                message: `${current.HRM2010_R01PastFilter.workDt}에 근태가 없는 재직자를 그날 근무조의 정시 출퇴근(정상출근)으로 만듭니다.`,
                confirmText: '적용',
                cancelText: '취소',
                onConfirm: () => {
                    closeConfirmModal();
                    void applyBulk(current.HRM2010_R01PastFilter.workDt, current.selectedHRM2010_R01?.empNo);
                },
                onCancel: closeConfirmModal,
            });
            setConfirmModalVisible(true);
        });
    }, [confirmDiscard, setConfirmModalProps, setConfirmModalVisible, closeConfirmModal, applyBulk]);
    /** 직전 조회 조건과 같은 Excel 파일을 내려받는다. */
    const handleExcelClick = useCallback(async () => {
        const state = useHRM2010_R01Store.getState();
        if (!hasFetched.current) {
            showWarning('먼저 조회하세요.');
            return;
        }
        const response = await downloadExcel_HRM2010_R01('search', toParams(state.HRM2010_R01PastFilter));
        if (!response.data.successFlag) return;
        downloadSettingExcel({
            responseData: response.data.data,
            fileName: `일일근태_${state.HRM2010_R01PastFilter.workDt}-excel.xlsx`,
        });
    }, []);
    /** 화면 상태와 메시지를 초기화하고 화면 기준 목록을 다시 조회한다. */
    const initContainerData = useCallback(() => {
        useHRM2010_R01Store.getState().initData();
        hasFetched.current = false;
        setMessage('');
        void loadMasters();
    }, [setMessage, loadMasters]);
    /** 위·아래 그리드에서 액션바 등록·삭제 대상을 바꾼다. */
    const setActiveGridType = useCallback((value: 'header' | 'detail') => {
        useHRM2010_R01Store.getState().setActiveGridType(value);
    }, []);
    return {
        fetchData,
        handleHeaderRowClick,
        handleDetailInsert,
        handleDeleteClick,
        handleSaveClick,
        handleBulkClick,
        handleExcelClick,
        handleEventAddClick,
        initContainerData,
        setActiveGridType,
    };
};
